import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import { configureAuthClient } from "@pathwright/auth/src/api/client"
import { createCache } from "@pathwright/graphql-client"
import { getApolloClientVersion } from "@pathwright/utils/src/client-build/clientBuildMeta"
import {
  createApolloOperationLink,
  handleApolloOperationError,
  registerApolloClient
} from "@pathwright/utils/src/graphql/apolloOperation"
import {
  createConnectionGateLink,
  createGraphqlRetryAttempts,
  createRetryRecoveryLink
} from "@pathwright/utils/src/graphql/connectionGate"
import { clearInvalidToken } from "@pathwright/utils/src/token/utils"
import { captureMessage } from "@sentry/browser"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { RefreshToken } from "../auth/refresh-token/useRefreshToken"
import { LocalDevSSO } from "../auth/useLocalDevSSO"
import { getEnv, getIsLocalDev, getPathwrightDomain } from "../lib/env"
import ApolloClientStoreBridge from "./ApolloClientStoreBridge"
import { currentAuthTokenLink } from "./currentAuthTokenLink"
import { usePathwrightContext } from "./PathwrightContext"
import {
  developmentClientLink,
  refreshedAuthTokenLink
} from "./refreshedAuthTokenLink"

export const getGraphQLEndpoint = () => {
  // Accept a full endpoint URL or a local HTTP host as well as a deployed hostname.
  const explicitHost = process.env.REACT_APP_GQL
  if (
    explicitHost &&
    (/^https?:\/\//.test(explicitHost) ||
      /^(localhost|127\.0\.0\.1)(:|$)/.test(explicitHost))
  ) {
    const base = /^https?:\/\//.test(explicitHost)
      ? explicitHost
      : `http://${explicitHost}`
    return `${base.replace(/\/$/, "")}/`
  }
  // returns the proper graphql server endpoint based on the current env

  let isLocalDev = getIsLocalDev()

  let domain = ``
  const pathwrightDomain = getPathwrightDomain()

  if (process.env.REACT_APP_GQL) {
    domain = process.env.REACT_APP_GQL
    isLocalDev = false
  } else if (pathwrightDomain) {
    // one of pathwright's domains
    domain = `gql.${pathwrightDomain}.com`
  } else {
    // custom domain
    domain = "gql.pathwright.com"
  }

  // if (isLocalDev) {
  //   // ROBERT TESTING WHILE TRAVELING
  //   return `https://gql.pathwrightstaging.com/`
  // }

  const PORT = process.env.REACT_APP_GQL_PORT || process.env.SERVER_PORT || 8000

  const base = isLocalDev
    ? `http://${window.location.hostname}:${PORT}/`
    : `https://${domain}/`

  return base
}

// Mutations safe to send twice when the first response was lost: a second
// request applies the same result rather than creating a new record.
const FETCH_FAILURE_RETRYABLE_MUTATIONS = new Set(["CompletePathItem"])

type Client = ApolloClient<any> & { _consistentReadClient: ApolloClient<any> }

// This is a singleton pattern
let _client = null as unknown as Client

type GetApolloClientOptions = {
  schoolId?: number
}

const GRAPHQL_PATH = "/graphql" as const

/** Read the active client, creating it on first use. The provider owns replacement. */
export const getApolloClient = (options: GetApolloClientOptions = {}) => {
  // If in "test" env, attempt to use the window.testClient
  // TODO: should this rather be handled via jest mocks?
  if (getEnv("test") && window.testClient && _client !== window.testClient) {
    _client = window.testClient as Client
  }

  return _client || createApolloClient(options.schoolId)
}

// Each client owns its session data; only normalization policies are shared.
const createApolloClient = (schoolId?: number) => {
  const operationLink = createApolloOperationLink(GRAPHQL_PATH)

  const httpLink = createHttpLink({
    uri: (operation) => {
      let uri = `${getGraphQLEndpoint()}graphql?school_id=${schoolId}`

      // Supply an operationName as a convenience for identifying the operation
      // via the endpoint, which, for instance, is all that is included in
      // Sentry breadcrumbs. So, this can help with debugging.
      if (operation.operationName) {
        uri += `&operationName=${operation.operationName}`
      }

      return uri
    }
  })

  const schoolLink = setContext((_, { headers }) => {
    let nextHeaders = { ...headers }
    nextHeaders.school_id = schoolId
    // return the headers to the context so httpLink can read them
    return {
      headers: nextHeaders
    }
  })

  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = localStorage.auth_token

    let nextHeaders = { ...headers }

    if (token) {
      nextHeaders.authorization = `Bearer ${token}`
    }
    // return the headers to the context so httpLink can read them
    return {
      headers: nextHeaders
    }
  })

  // Retry transient network errors on queries. Skip most mutations: composers and
  // other forms restore their draft on failure so the user can retry immediately,
  // and silent retries delay that feedback and can double-submit if the first
  // request actually succeeded. Allowlisted mutations retry once on a fetch
  // failure with no HTTP response.
  // https://www.apollographql.com/docs/link/links/retry
  const retryLink = new RetryLink({
    attempts: createGraphqlRetryAttempts(FETCH_FAILURE_RETRYABLE_MUTATIONS)
  })

  const retryRecoveryLink = createRetryRecoveryLink((operation) => {
    captureMessage(
      `${operation.operationName} recovered after fetch failure`,
      "info"
    )
  })

  const connectionGateLink = createConnectionGateLink(
    `${getGraphQLEndpoint()}graphql?school_id=${schoolId}`
  )

  const errorLink = onError((options) => {
    const { graphQLErrors } = options

    handleApolloOperationError(options, { graphqlPath: GRAPHQL_PATH })

    // GraphQL is on another origin, so retrying without this token is
    // anonymous, not session-authenticated. Leave for sign-in instead of
    // keeping a signed-in shell that no longer has a credential.
    if (
      graphQLErrors &&
      graphQLErrors.some(
        ({ message }) =>
          message ===
            `Invalid token. An error occurred while verifying the token: The provided token was revoked.` ||
          message ===
            `Invalid token. An error occurred while verifying the token: The provided token could not be found in the database.`
      ) &&
      localStorage.getItem("auth_token")
    ) {
      clearInvalidToken()
      return
    }
  })

  const clientOptions = {
    link: from([
      operationLink,
      retryRecoveryLink,
      retryLink,
      schoolLink,
      developmentClientLink,
      currentAuthTokenLink,
      authLink,
      errorLink,
      refreshedAuthTokenLink,
      connectionGateLink,
      httpLink
    ]),
    cache: createCache(),
    name: "web",
    version: getApolloClientVersion() ?? "1.0"
  }

  _client = new ApolloClient(clientOptions) as Client

  // Optional client used for ensuring GQL server reads data from the main database
  // as opposed to the read replica database.
  // _client._consistentReadClient = new ApolloClient({
  //   ...clientOptions,
  //   link: from([retryLink, consistentReadMiddlewareLink, httpLink])
  // })
  _client._consistentReadClient = _client

  return _client
}

export const usePathwrightClient = () => {
  const { school } = usePathwrightContext()
  const client = getApolloClient({ schoolId: school.id })
  return client
}

type PathwrightClientProps = {
  schoolId: number
  onAuthChange?: () => void
  /**
   * Optional consumer-generated key (NOT the refresh token) that when changed
   * resets the client and its cached auth data without remounting its children.
   * Allows components like the sandbox to respond to auth changes
   * and clear leftover data that would cause sign-in issues..
   * School changes replace the client independently of this key.
   */
  sessionKey?: string | number
  children: JSX.Element | (({ client }: { client: Client }) => JSX.Element)
}

export const PathwrightClient = ({
  schoolId,
  sessionKey,
  children,
  onAuthChange
}: PathwrightClientProps) => {
  const [client, setClient] = useState<Client | null>(null)
  useEffect(() => {
    // Retire observers from the old session. Its outstanding work cannot mutate
    // the replacement's cache, so no asynchronous cache cleanup is required.
    if (_client) {
      new Set([_client, _client._consistentReadClient]).forEach((item) =>
        item.stop()
      )
    }
    const client = createApolloClient(schoolId)
    const releaseAuthClient = configureAuthClient(() => client)
    registerApolloClient(GRAPHQL_PATH, client)
    setClient(client)
    window.apolloClientStoreBridge = new ApolloClientStoreBridge(client)
    return releaseAuthClient
  }, [schoolId, sessionKey])

  return client ? (
    <ApolloProvider client={client}>
      <LocalDevSSO onAuthChange={onAuthChange}>
        <RefreshToken />
        {typeof children === "function" ? children({ client }) : children}
      </LocalDevSSO>
    </ApolloProvider>
  ) : null
}

PathwrightClient.displayName = "PathwrightClient"

PathwrightClient.propTypes = {
  schoolId: PropTypes.number.isRequired,
  onAuthChange: PropTypes.func
}

export default function withPathwrightClient(ComposedComponent: any) {
  const ComposedPathwrightClient = ({
    schoolId,
    onAuthChange,
    ...props
  }: {
    schoolId: number
    onAuthChange?: () => void
  }) => (
    <PathwrightClient schoolId={schoolId} onAuthChange={onAuthChange}>
      <ComposedComponent {...props} onAuthChange={onAuthChange} />
    </PathwrightClient>
  )

  return ComposedPathwrightClient
}
